import Navbar from "@/components/navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, MessageSquare, FileText, Users, TrendingUp, Settings } from "lucide-react";
import { Link } from "wouter";
import { useState } from "react";

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState("");

  const helpCategories = [
    {
      icon: <Search className="h-6 w-6" />,
      title: "Getting Started",
      description: "Learn the basics of The MAX Platform™",
      articles: [
        "How to browse shows by artist, venue, or market",
        "Understanding artist performance metrics and analytics",
        "Creating your first sponsorship proposal",
        "Setting up your brand profile"
      ]
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Sponsorship Proposals",
      description: "Master the proposal creation process",
      articles: [
        "Using the AI-powered proposal generator",
        "Understanding pricing tiers and packages",
        "Best practices for proposal messaging",
        "Tracking proposal status and metrics"
      ]
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Audience Insights",
      description: "Leverage demographic and engagement data",
      articles: [
        "Reading artist audience demographics",
        "Understanding social media metrics",
        "Using advanced filtering options",
        "Interpreting engagement rates and growth"
      ]
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "Account Management",
      description: "Manage your profile and preferences",
      articles: [
        "Updating your company information",
        "Managing notification settings",
        "Adding team members and permissions",
        "Billing and subscription management"
      ]
    },
    {
      icon: <Settings className="h-6 w-6" />,
      title: "Technical Support",
      description: "Troubleshooting and platform issues",
      articles: [
        "Common login and access issues",
        "Browser compatibility requirements",
        "Data export and reporting features",
        "API access and integrations"
      ]
    },
    {
      icon: <MessageSquare className="h-6 w-6" />,
      title: "Best Practices",
      description: "Tips for successful music sponsorships",
      articles: [
        "Selecting the right artists for your brand",
        "Timing your sponsorship campaigns",
        "Measuring ROI and campaign success",
        "Building long-term artist partnerships"
      ]
    }
  ];

  const filteredCategories = helpCategories.filter(category =>
    category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.articles.some(article => article.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-24">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-neutral-900 mb-4">Help Center</h1>
          <p className="text-xl text-neutral-600 mb-8">
            Everything you need to know about using The MAX Platform™
          </p>
          
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-400" />
            <Input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 py-3 text-lg"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredCategories.map((category, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-max-brand/10 rounded-lg text-max-brand">
                    {category.icon}
                  </div>
                  <CardTitle className="text-lg">{category.title}</CardTitle>
                </div>
                <p className="text-neutral-600">{category.description}</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {category.articles.map((article, articleIndex) => (
                    <li key={articleIndex}>
                      <Link href="#" className="text-neutral-700 hover:text-max-brand transition-colors text-sm">
                        {article}
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">Still need help?</h2>
          <p className="text-neutral-600 mb-6">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/contact-us">
              <button className="bg-max-brand text-white px-6 py-3 rounded-lg hover:bg-max-brand/90 transition-colors">
                Contact Support
              </button>
            </Link>
            <button className="border border-neutral-300 text-neutral-700 px-6 py-3 rounded-lg hover:bg-neutral-50 transition-colors">
              Schedule a Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}